/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {ChangeDetectorRef, Component, Inject, NgZone, OnChanges} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import template from './ruleset-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {ADN_TARGETING_TYPES} from '../targeting/targeting-constants';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {AdUnitResource} from '../../components/api/resources/resource/adunit.resource';
import {EarningsAccountResource} from '../../components/api/resources/resource/earnings-account.resource';
import {LayoutResource} from '../../components/api/resources/resource/layout.resource';
import {SiteResource} from '../../components/api/resources/resource/site.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {SearchResource} from '../../components/api/resources/resource/search';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {layoutCategoriesFor} from './ruleset-layout-categories';

interface AdvertiserFloorPrice {
  type?: 'ADVERTISER' | 'TEAM';
  advertiser?: any;
  team?: any;
  floorPrice?: {amount?: number | null; currency?: string | null};
}

@Component({
  standalone: false,
  selector: 'adn-ruleset-edit-page',
  template
})
export class RulesetEditPageComponent extends AdminResourceEditPageBase implements OnChanges {
  readonly usageLists: {publishers: any; sites: any; adUnits: any} = {
    publishers: {},
    sites: {},
    adUnits: {}
  };

  readonly rulesetTypes = ['INVENTORY', 'TARGETING', 'SIZE', 'LAYOUT'];
  readonly targetingTypes = _.values(ADN_TARGETING_TYPES);
  readonly layoutCategories = ['FULL_PAGE', 'POLITICAL', 'HTML', 'VAST', 'NATIVE', 'THIRD_PARTY', 'STANDARD', 'DATA', 'IMAGE'];

  allTeams: any[] | null = null;
  allLayouts: any[] = [];
  layoutNames: Record<string, any> = {};
  advertiserSearchResults: any[] = [];
  advertiserTeamSearchResults: any[] = [];
  canUpdateSiteRuleset = false;
  teamsWarning = false;
  showSingular = true;
  singularTeam: any = null;
  includeHighImpact = false;
  showHighImpact = false;
  layoutsExpanded = false;
  layoutCategoriesExpanded = false;

  private listInitialized = false;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    private adnListHelper: AdnListHelperService,
    @Inject(EarningsAccountResource) private EarningsAccount: any,
    @Inject(SiteResource) private Site: any,
    @Inject(AdUnitResource) private AdUnit: any,
    @Inject(TeamResource) private Team: any,
    @Inject(LayoutResource) private Layout: any,
    @Inject(SearchResource) private searchResource: any,
    private readonly translate: TranslateService,
    private cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.canUpdateSiteRuleset = LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN')
      || LocalUserPermissions.hasAny('AX_PUBLISHER_TEAM_ADMIN')
      || LocalUserPermissions.hasAny('MANAGE_PUBLISHING');
    this.deferFormDirtyTracking();

    this.Team.query({minimal: true}).$promise.then((page: any) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.allTeams = _.filter(page.results, (team: any) => {
          return team.type === 'STANDARD' || team.type === 'AX_PUBLISHER';
        });
        if (this.isNew && this.allTeams.length === 1 && !this.singularTeam) {
          this.singularTeam = this.allTeams[0];
        }
        this.resolveSingularTeamReference();
        this.resumeFormDirtyTracking();
      });
    });

    this.Layout.query({}).$promise.then((page: any) => {
      this.ngZone.run(() => {
        this.allLayouts = _.map(page.results || [], (layout: any) => this.enrichLayoutWithCategories(layout));
        this.layoutNames = _.keyBy(this.allLayouts, 'id');
      });
    });
  }

  ngOnChanges(): void {
    super.ngOnChanges();
    if (this.model?.id && !this.listInitialized) {
      this.listInitialized = true;
      const moreParams = {siteRuleset: this.model.id};
      this.adnListHelper.setUpBasicList(this.usageLists.publishers, this.EarningsAccount, 'fullPublishersForSiteRuleset', {
        moreParams,
        baseTable: 'earningsAccount'
      });
      this.adnListHelper.setUpBasicList(this.usageLists.sites, this.Site, 'fullSitesForSiteRuleset', {
        moreParams,
        baseTable: 'site'
      });
      this.adnListHelper.setUpBasicList(this.usageLists.adUnits, this.AdUnit, 'fullAdUnitsForSiteRuleset', {
        moreParams,
        baseTable: 'adUnit'
      });
      if (!this.isNew) {
        this.usageLists.publishers.makeQuery?.();
        this.usageLists.sites.makeQuery?.();
        this.usageLists.adUnits.makeQuery?.();
      }
    }
  }

  protected afterInitEditingModel(): void {
    if (this.isNew) {
      this.editingModel.type = this.editingModel.type || 'INVENTORY';
    }
    this.initialiseFloorPrices();
    this.initialiseUiSizes();
    this.ensureTeamSelections();
    this.resolveSingularTeamReference();
    this.layoutsExpanded = this.isNew;
    this.layoutCategoriesExpanded = this.isNew;
  }

  protected afterInitAfterSave(saveObj?: any): void {
    const teams = _.get(saveObj || this.editingModel, 'teams') || [];
    this.singularTeam = teams[0] || null;
    this.editingModel.teams = _.map(teams, 'id');
    this.editingModel.layouts = _.map(_.get(saveObj || this.editingModel, 'layouts') || [], 'id');
    this.initialiseFloorPrices(saveObj);
    this.initialiseUiSizes(saveObj);
    this.showSingular = (this.editingModel.teams || []).length <= 1;
    this.resolveSingularTeamReference();
    if (!this.isNew) {
      this.usageLists.publishers.makeQuery?.();
      this.usageLists.sites.makeQuery?.();
      this.usageLists.adUnits.makeQuery?.();
    }
  }

  protected applySave(resource: any): void {
    const previousTeamCount = (this.editingModel.teams || []).length;
    super.applySave(resource);
    this.teamsWarning = previousTeamCount !== (_.get(resource, 'teams') || []).length;
  }

  protected onBeforeSubmit(): void {
    this.editingModel.advertiserFloorPrices = _.filter(this.editingModel.advertiserFloorPrices, (fp: AdvertiserFloorPrice) => {
      const type = _.get(fp, 'type');
      if (_.get(fp, ['floorPrice', 'amount']) >= 0 && _.get(fp, ['floorPrice', 'currency', 'length']) === 3 && type) {
        if (type === 'ADVERTISER') {
          return _.get(fp, ['advertiser', 'id']);
        }
        if (type === 'TEAM') {
          return _.get(fp, ['team', 'id']);
        }
      }
      return false;
    });
    this.editingModel.advertiserFloorPrices = _.map(this.editingModel.advertiserFloorPrices, (fp: AdvertiserFloorPrice) => {
      const type = _.get(fp, 'type');
      if (type === 'ADVERTISER' && fp.team) {
        delete fp.team;
      } else if (type === 'TEAM' && fp.advertiser) {
        delete fp.advertiser;
      }
      return fp;
    });
    this.editingModel.sizes = _.filter(this.editingModel.uiSizes, (size: any) => {
      const dimensions = _.get(size, 'dimensions');
      if (dimensions) {
        return _.get(dimensions, 'width') || (_.isArray(dimensions) && dimensions.length === 2);
      }
      return false;
    });
    if (this.includeHighImpact) {
      this.editingModel.sizes.push({dimensions: [0, 0]});
    }
    if (!_.has(this.editingModel, ['floorPrice', 'amount']) || _.get(this.editingModel, ['floorPrice', 'amount']) === null) {
      this.editingModel.floorPrice = null;
    }
    if (this.showSingular) {
      this.editingModel.teams = this.singularTeam ? [this.singularTeam] : [];
    }
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  get visibleLayoutCategories(): string[] {
    return this.layoutCategories.filter((category) => this.isLayoutCategoryVisible(category));
  }

  private enrichLayoutWithCategories(layout: any): any {
    const categories = layoutCategoriesFor(layout);
    const categorySummary = _.map(categories, (category) => {
      return this.translate.instant('layoutCategory.' + category);
    }).join(', ');
    const categoryHelp = categorySummary
      ? `${this.translate.instant('label.layoutCategory')}: ${categorySummary}`
      : '';
    return {
      ...layout,
      categories,
      categoryHelp
    };
  }

  private isLayoutCategoryVisible(category: string): boolean {
    if ((this.editingModel.layoutCategories || []).includes(category)) {
      return true;
    }
    switch (category) {
      case 'DATA':
        return false;
      case 'FULL_PAGE':
        return this.LocalNetworkProfile.isPureMarketplacePlatform();
      case 'POLITICAL':
        return this.LocalNetworkProfile.isPoliticalAdsPlatform();
      default:
        return true;
    }
  }

  selectTeams(): void {
    this.editingModel.teams = _.map(this.allTeams || [], 'id');
    this.onFieldChange();
  }

  deselectTeams(): void {
    this.editingModel.teams = [];
    this.onFieldChange();
  }

  removeFloorPrices(index: number): void {
    _.pullAt(this.editingModel.advertiserFloorPrices, index);
    this.initialiseFloorPrices();
    this.onFieldChange();
  }

  addFloorPrices(): void {
    this.editingModel.advertiserFloorPrices = this.editingModel.advertiserFloorPrices || [];
    this.editingModel.advertiserFloorPrices.push({type: 'ADVERTISER'});
    this.onFieldChange();
  }

  removeDimensions(index: number): void {
    _.pullAt(this.editingModel.uiSizes, index);
    this.initialiseUiSizes();
    this.onFieldChange();
  }

  addDimensions(): void {
    this.editingModel.uiSizes = this.editingModel.uiSizes || [];
    this.editingModel.uiSizes.push({dimensions: []});
    this.onFieldChange();
  }

  advertiserSearch = (searchElement: AdnSelectSearchElement): void => {
    this.searchResource.query({q: searchElement.search, types: 'Advertiser'}).then((data: any) => {
      this.ngZone.run(() => {
        this.advertiserSearchResults = data.searchResults;
      });
    });
  };

  advertiserTeamSearch = (searchElement: AdnSelectSearchElement): void => {
    this.Team.query({
      minimal: true,
      filterBy: 'name;description',
      filterByLike: searchElement.search,
      where: 'type = AX_ADVERTISER'
    }).$promise.then((page: any) => {
      this.ngZone.run(() => {
        this.advertiserTeamSearchResults = page.results || [];
      });
    });
  };

  canRemoveFloorPrice(index: number, advPrice: AdvertiserFloorPrice): boolean {
    return !(index === 0
      && (this.editingModel.advertiserFloorPrices || []).length < 2
      && !advPrice?.advertiser);
  }

  private ensureTeamSelections(): void {
    this.editingModel.teams = _.isArray(this.editingModel.teams) ? this.editingModel.teams : [];
    this.editingModel.layouts = _.isArray(this.editingModel.layouts) ? this.editingModel.layouts : [];
    this.editingModel.layoutCategories = _.isArray(this.editingModel.layoutCategories) ? this.editingModel.layoutCategories : [];
    this.editingModel.targetingTypes = _.isArray(this.editingModel.targetingTypes) ? this.editingModel.targetingTypes : [];
  }

  private initialiseFloorPrices(data?: any): void {
    this.editingModel.advertiserFloorPrices = _.isArray(_.get(data || this.editingModel, 'advertiserFloorPrices'))
      ? _.get(data || this.editingModel, 'advertiserFloorPrices')
      : [];
    this.editingModel.advertiserFloorPrices[0] = this.editingModel.advertiserFloorPrices[0] ? this.editingModel.advertiserFloorPrices[0] : {};
    this.editingModel.advertiserFloorPrices = _.map(this.editingModel.advertiserFloorPrices, (advFloor: AdvertiserFloorPrice) => {
      if (_.get(advFloor, ['team', 'id'])) {
        advFloor.type = 'TEAM';
      } else {
        advFloor.type = 'ADVERTISER';
      }
      return advFloor;
    });
  }

  private initialiseUiSizes(data?: any): void {
    const sizes = _.isArray(_.get(data || this.editingModel, 'sizes')) ? _.get(data || this.editingModel, 'sizes') : [];
    sizes[0] = sizes[0] ? sizes[0] : {dimensions: []};
    this.editingModel.uiSizes = _.cloneDeep(sizes);
    const highImpactIndex = _.findIndex(this.editingModel.uiSizes, (uiSize: any) => {
      return uiSize.dimensions?.[0] === 0 && uiSize.dimensions?.[1] === 0;
    });
    this.includeHighImpact = highImpactIndex > -1;
    if (highImpactIndex > -1) {
      _.pullAt(this.editingModel.uiSizes, highImpactIndex);
    }
    const fullPageLayouts = LocalNetworkInfo.obtain().getInfo('idsOfFullPageLayouts');
    const hasFullPageLayouts = _.isArray(fullPageLayouts)
      ? fullPageLayouts.length > 0
      : !!fullPageLayouts;
    this.showHighImpact = hasFullPageLayouts || this.includeHighImpact;
  }

  private resolveSingularTeamReference(): void {
    if (!this.showSingular || !this.allTeams?.length) {
      return;
    }
    if (this.singularTeam && !_.isString(this.singularTeam)) {
      const match = _.find(this.allTeams, ['id', this.singularTeam.id]);
      if (match) {
        this.singularTeam = match;
      }
      return;
    }
    const teamId = this.singularTeam || _.get(this.editingModel, ['teams', 0]);
    if (!teamId) {
      return;
    }
    const resolvedId = _.isString(teamId) ? teamId : teamId.id;
    const match = _.find(this.allTeams, ['id', resolvedId]);
    if (match) {
      this.singularTeam = match;
    }
  }
}
